import { usePermissions } from '@/hooks/usePermissions'
import { useTooltipAnchor } from '@/hooks/useTooltipAnchor'
import { useTranslation } from 'react-i18next'

import { AddIcon, Button, Box as DarkBox, Tooltip } from '@hivewatch/dark'

import { useAppSelector } from '@/redux/hooks'
import { selectMe } from '@/redux/me/meSlice'
import { trackEvent } from '@/utils/analytics'
import { tVar } from '@/utils/i18n'

import { FLOOR_PLAN_CHECKPOINTS_ANALYTICS } from '../../constants/constants'
import { darkCheckPointsPanelStyles } from './DarkCheckPointsPanel.styles'

interface DarkCheckPointListHeaderIProps {
  colorScheme: 'dark' | 'light'
  facilityId: string
  checkpointCount: number
  selectedCount: number
  hasFloorPlan: boolean
  isEditModeSatisfied: boolean
  onCreateClick: () => void
  onPrintClick: () => void
}

/**
 * Header slot for the Checkpoints list rail: plain title with no leading icon
 * (ENG-31744 precedent), the permission-aware description, and the two primary
 * actions. Fires the same Mixpanel events the legacy panel fires.
 */
export const DarkCheckPointListHeader = ({
  colorScheme,
  facilityId,
  checkpointCount,
  selectedCount,
  hasFloorPlan,
  isEditModeSatisfied,
  onCreateClick,
  onPrintClick,
}: DarkCheckPointListHeaderIProps) => {
  const { t } = useTranslation()
  const me = useAppSelector(selectMe)
  const { shouldEnableCreateCheckpoint } = usePermissions()
  const s = darkCheckPointsPanelStyles(colorScheme)
  // The disabled button swallows pointer events, so the tooltip anchors on a
  // wrapper box instead of the button itself. The wrapper is only focusable
  // while the button is disabled, so keyboard users can reach the reason
  // without adding a tab stop when the button works.
  const { anchorEl, onMouseEnter, onMouseLeave, onFocus, onBlur } =
    useTooltipAnchor<HTMLDivElement>()

  return (
    <DarkBox {...s.header}>
      <DarkBox as='h2' {...s.headerTitle}>
        {t('floors:checkpoint_list_title')}
      </DarkBox>
      <DarkBox {...s.headerDescription}>
        {shouldEnableCreateCheckpoint
          ? t('floors:checkpoint_list_description_admin')
          : t('floors:checkpoint_list_description')}
      </DarkBox>
      <DarkBox {...s.headerButtons}>
        <DarkBox
          data-testid='darkCheckpointList_createButtonAnchor'
          onBlur={onBlur}
          onFocus={onFocus}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          {...(!hasFloorPlan && {
            'aria-label': t('floors:checkpoint_create_no_floor_plan_tooltip'),
            tabIndex: 0,
          })}
        >
          <Button
            colorScheme={colorScheme}
            data-testid='darkCheckpointList_createButton'
            disabled={
              !isEditModeSatisfied ||
              !shouldEnableCreateCheckpoint ||
              !hasFloorPlan
            }
            leftIcon={<AddIcon size={10} />}
            onClick={() => {
              onCreateClick()
              trackEvent(FLOOR_PLAN_CHECKPOINTS_ANALYTICS.createCheckpoint, {
                facility_id: facilityId,
                role: me?.roleName,
              })
            }}
            variant='primary'
          >
            {t('floors:checkpoint_create_button')}
          </Button>
        </DarkBox>
        {!hasFloorPlan && (
          <Tooltip
            anchorEl={anchorEl}
            colorScheme={colorScheme}
            label={t('floors:checkpoint_create_no_floor_plan_tooltip')}
            placement='bottom'
          />
        )}
        <Button
          colorScheme={colorScheme}
          data-testid='darkCheckpointList_printButton'
          disabled={checkpointCount === 0}
          onClick={() => {
            onPrintClick()
            trackEvent(FLOOR_PLAN_CHECKPOINTS_ANALYTICS.printQrCodes, {
              facility_id: facilityId,
              role: me?.roleName,
              selected_count: selectedCount,
              total_count: checkpointCount,
            })
          }}
          variant='secondary'
        >
          {selectedCount > 0
            ? tVar(t, 'floors:checkpoint_print_selected', { selectedCount })
            : t('floors:checkpoint_print_all')}
        </Button>
      </DarkBox>
    </DarkBox>
  )
}
